import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import Panzoom, { PanzoomObject } from '@panzoom/panzoom'

import FilterContext from '@/components/pages/production/components/context'
import ProductionFooter from '@/components/pages/production/components/footer/component'
import MoreInfoButton from '@/components/pages/production/components/more-info-button'
import { MAP_VALUES } from '@/constants'
import MapContext from '@/context/map'
import useWindowSize, { isTypeDesktop } from '@/hooks/use-window-size'

import ClearSectionsButton from '../clear-sections-button'
import SimpleMap, { SimpleMapProps } from '../simple-map'

import styles from './styles.module.scss'

interface MapProps extends SimpleMapProps {
    displayFooter?: boolean
}

const SvgPanZoom: FC<MapProps> = ({ displayFooter = false, ...props }) => {
    const {
        dispatch: { setIsDragging, setIsDoubleClicked },
        selectors: { mapState },
    } = useContext(MapContext)

    const {
        selectors: { getHighlightedSections },
        dispatch: { onResetSectionSelectionAction, onSetSelectSimilarSnackbarVisibility },
    } = useContext(FilterContext)

    const [mapPanZoom, setMapPanZoom] = useState<PanzoomObject>()

    const container = useRef<HTMLDivElement>(null)
    const svgImage = useRef<SVGSVGElement>(null)

    const { type } = useWindowSize()
    const isDesktop = isTypeDesktop(type)

    useEffect(() => {
        const containerElement = container.current
        const svgImageElement = svgImage.current

        if (!containerElement || !svgImageElement) {
            return
        }

        const panZoomObject: PanzoomObject = Panzoom(svgImageElement, {
            minScale: MAP_VALUES.MIN_SCALE,
            canvas: true,
            step: MAP_VALUES.STEP,
        })

        svgImageElement.addEventListener('panzoomchange', () => {
            setIsDragging?.(true)
        })

        svgImageElement.addEventListener('panzoomstart', () => {
            setIsDragging?.(false)
        })

        containerElement.addEventListener('wheel', (event) => {
            panZoomObject.zoomWithWheel(event)
        })

        setMapPanZoom(panZoomObject)
    }, [container, svgImage])

    useEffect(() => {
        if (mapState.isDoubleClicked) {
            mapPanZoom?.zoomIn({ step: 0.25 })
            setIsDoubleClicked(false)
        }
    }, [mapState.isDoubleClicked])

    const handleDismissSectionSnackbar = () => {
        onSetSelectSimilarSnackbarVisibility({ showSnackbar: false })
        onResetSectionSelectionAction()
    }

    return (
        <div className={styles.mapContainer} data-root="svg-pan-zoom" data-testid="map-container" ref={container}>
            <SimpleMap {...props} ref={svgImage} />
            {displayFooter ? <ProductionFooter renderOnDesktop open /> : null}
            {getHighlightedSections.length >= 1 && (
                <div className={styles.mapControlsContainer} data-testid="map-controls-container">
                    <ClearSectionsButton onClick={handleDismissSectionSnackbar} />
                </div>
            )}

            {!isDesktop && <MoreInfoButton className={styles.moreInfoButton} fontSize="1.125rem" />}
        </div>
    )
}

export default SvgPanZoom
